import React, { useEffect, useState, useMemo, useRef } from "react";
import "./Exercises.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faFileAudio,
  faStar,
  faCircleCheck,
  faChevronRight,
  faPlay,
  faDownload,
  faXmark,
  faCheck,
  faBookOpen,
  faArrowLeft,
  faBookmark,
  faSpinner,
} from "@fortawesome/free-solid-svg-icons";
import { MainHeader } from "../../chatMain/MainHeader/MainHeader.jsx";
import { SidebarCol } from "../../chatMain/Sidebar/Sidebar.jsx";
import ExerciseIllustration from "../../../assets/ExerciseIllustration.png";
import { BlogSection } from "../BlogSection/BlogSection.jsx";
import { useParams, Link } from "react-router-dom";
import AudioPlayer from "react-h5-audio-player";
import "react-h5-audio-player/lib/styles.css";
import api from "../../../axiosconfig.js";
import { toast } from "react-toastify";
import { getGuidanceAttribution } from "../../../utils/guidanceAttribution.js";
import { useMindfulnessMusic } from "../../../context/MindfulnessMusicContext";
import { MindfulnessMusicBar } from "../../../components/common/MindfulnessMusicBar/MindfulnessMusicBar";

export const Exercises = () => {
  const { id } = useParams();
  const iframeRef = useRef(null);
  const { pauseForGuidance, resumeAfterGuidance } = useMindfulnessMusic();

  // Resume background music when leaving the guidance screen or when component unmounts
  useEffect(() => {
    return () => {
      resumeAfterGuidance();
    };
  }, [resumeAfterGuidance]);

  const [data, setData] = useState(null);
  const [duration, setDuration] = useState(0);
  const [exerciseCompleted, setExerciseCompleted] = useState(false);
  const [exerciseFavourited, setExerciseFavourited] = useState(false);
  const [showAudioPlayer, setShowAudioPlayer] = useState(false);
  const [isAudioBuffering, setIsAudioBuffering] = useState(false);
  const [isPlayingAudio, setIsPlayingAudio] = useState(false);
  const [audioErrorMsg, setAudioErrorMsg] = useState("");
  const [activeTab, setActiveTab] = useState("overview"); // "overview" | "practice" | "homework"
  const [journalText, setJournalText] = useState("");
  const [journalSaved, setJournalSaved] = useState(false);

  const API_URL = process.env.REACT_APP_API_URL;

  const attribution = useMemo(() => {
    if (!data) return null;
    return (
      (data.courtesy && { creator: data.creator, courtesy: data.courtesy }) ||
      getGuidanceAttribution({
        title: data.title,
        defaulturl: id,
        creator: data.creator,
        courtesy: data.courtesy,
      })
    );
  }, [data, id]);

  const courtesyText = attribution?.courtesy || (data?.creator ? `Courtesy of ${data.creator}` : null);
  const creatorName = attribution?.creator || (courtesyText ? courtesyText.replace(/^Courtesy of\s+/i, "") : null);

  const cleanedTitle = useMemo(() => {
    return (data?.title || "Mindfulness Guidance").trim();
  }, [data]);

  const cleanedCategory = useMemo(() => {
    return (data?.category || "Practice").trim();
  }, [data]);

  const cleanedSummary = useMemo(() => {
    return (data?.summary || "").replace(/["']+$/, "").trim();
  }, [data]);

  const isDirectAudio = useMemo(() => {
    if (!data?.externalLink) return false;
    const url = data.externalLink.toLowerCase();
    return (
      url.endsWith(".mp3") ||
      url.includes(".mp3?") ||
      url.endsWith(".m4a") ||
      url.includes(".m4a?") ||
      url.endsWith(".wav") ||
      (data.externalFormat === "audio" && !url.includes("tarabrach.com"))
    );
  }, [data]);

  const isYouTube = useMemo(() => {
    if (!data?.externalLink) return false;
    const url = data.externalLink.toLowerCase();
    return (
      data.externalFormat === "youtube" ||
      url.includes("youtube.com") ||
      url.includes("youtu.be")
    );
  }, [data]);

  const youtubeEmbedUrl = useMemo(() => {
    if (!isYouTube || !data?.externalLink) return data?.externalLink;
    let url = data.externalLink;
    const separator = url.includes("?") ? "&" : "?";
    if (!url.includes("enablejsapi=1")) {
      url = `${url}${separator}enablejsapi=1&origin=${encodeURIComponent(
        typeof window !== "undefined" ? window.location.origin : ""
      )}`;
    }
    return url;
  }, [isYouTube, data]);

  const activeAudioUrl = useMemo(() => {
    if (data?.local === "yes") return data?.audiourl || "/ExerciseFiles/default.mp3";
    if (isDirectAudio) return data?.externalLink;
    return null;
  }, [data, isDirectAudio]);

  const hasAudioSession = Boolean(activeAudioUrl);

  // Auto-pause background music when YouTube plays or when user interacts with the iframe player
  useEffect(() => {
    const handleWindowMessage = (event) => {
      try {
        let payload = event.data;
        if (typeof payload === "string") {
          try {
            payload = JSON.parse(payload);
          } catch (e) {
            return;
          }
        }
        if (payload?.event === "infoDelivery" && payload?.info) {
          const state = payload.info.playerState;
          // YouTube playerState: 1 = PLAYING, 3 = BUFFERING
          if (state === 1 || state === 3) {
            pauseForGuidance();
          } else if (state === 2 || state === 0) {
            // 2 = PAUSED, 0 = ENDED
            resumeAfterGuidance();
          }
        }
      } catch (err) {
        // Ignore cross-origin message parsing errors
      }
    };

    const handleWindowBlur = () => {
      setTimeout(() => {
        if (document.activeElement && document.activeElement.tagName === "IFRAME") {
          pauseForGuidance();
        }
      }, 150);
    };

    window.addEventListener("message", handleWindowMessage);
    window.addEventListener("blur", handleWindowBlur);

    return () => {
      window.removeEventListener("message", handleWindowMessage);
      window.removeEventListener("blur", handleWindowBlur);
    };
  }, [pauseForGuidance, resumeAfterGuidance]);

  const checkFavourite = async (title) => {
    const exTitle = title || cleanedTitle;
    try {
      if (exTitle && exTitle !== "Invalid Exercise") {
        const response = await api.post(`${API_URL}/exercises`, {
          request: "checkFavourite",
          exercise: exTitle,
        });
        if (response?.data?.result === true) {
          setExerciseFavourited(true);
        }
      }
    } catch (error) {
      console.log("Error checking favourite:", error);
    }
  };

  useEffect(() => {
    let isMounted = true;

    const jsonURL = `/ExerciseFiles/${id}.json`;
    fetch(jsonURL)
      .then((res) => res.json())
      .then((items) => {
        if (!isMounted) return;
        if (Array.isArray(items) && items.length > 0) {
          const item = items[0];
          setData(item);
          setDuration(item.duration || 0);
          checkFavourite(item.title);
        } else {
          setData({
            title: "Invalid Exercise",
            summary: "Exercise could not be found.",
            category: "General",
            local: "no",
          });
        }
      })
      .catch((error) => {
        if (!isMounted) return;
        console.error("Error fetching exercise:", error);
        toast.error("Failed to load exercise content.");
      });

    return () => {
      isMounted = false;
    };
  }, [id]);

  const completeExercise = async () => {
    try {
      if (data && data.title !== "Invalid Exercise") {
        await api.post(`${API_URL}/exercises`, {
          request: "complete",
          exercise: id,
          duration: duration,
        });
        toast.success("Exercise marked as complete!");
        setExerciseCompleted(true);
      }
    } catch (error) {
      console.error("Error completing exercise:", error);
      toast.error("Could not update exercise completion.");
    }
  };

  const handleFavourite = async () => {
    if (!data || data.title === "Invalid Exercise") return;
    try {
      if (!exerciseFavourited) {
        await api.post(`${API_URL}/exercises`, {
          request: "favourite",
          exercise: cleanedTitle,
        });
        toast.success("Exercise added to favourites.");
        setExerciseFavourited(true);
      } else {
        await api.post(`${API_URL}/exercises`, {
          request: "unfavourite",
          exercise: cleanedTitle,
        });
        toast.success("Exercise removed from favourites.");
        setExerciseFavourited(false);
      }
    } catch (error) {
      console.error("Error toggling favourite:", error);
      toast.error("Could not update favourite status.");
    }
  };

  const handleSaveJournal = () => {
    setJournalSaved(true);
    toast.success("Journal reflection saved!");
    setTimeout(() => {
      setJournalSaved(false);
    }, 2500);
  };

  const downloadJournal = () => {
    const today = new Date();
    const datestring = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;
    const filename = `${datestring}_${cleanedTitle.replace(/\s+/g, "_")}_Journal.txt`;
    const textToWrite = journalText.trim() || "No notes recorded.";
    const textFileAsBlob = new Blob([textToWrite], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(textFileAsBlob);

    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Determine sections based on activeTab
  const currentSections = useMemo(() => {
    if (!data) return [];
    if (activeTab === "overview") {
      return data.overview?.sections || [];
    }
    if (activeTab === "practice") {
      return data.practice?.sections || [];
    }
    if (activeTab === "homework") {
      return data.homework?.sections || [];
    }
    return [];
  }, [data, activeTab]);

  return (
    <div className="exercise-page">
      <div className="sidebar">
        <SidebarCol />
      </div>

      <div className="exercise-container-wrapper">
        <div className="exercise-header">
          <MainHeader
            page={"mindfulness"}
            titleText={"Mindfulness Guidance"}
            subtitleText={
              "Find focus, reduce stress, and stay present with simple daily practices."
            }
          />
        </div>

        <div className="exercise-content-container">
          <div className="exercise-main-content-wrapper">
            {/* Breadcrumb Navigation */}
            <div className="exercise-breadcrumb">
              <Link to="/mindfulness" className="breadcrumb-link">
                <FontAwesomeIcon icon={faArrowLeft} className="breadcrumb-back-icon" />
                <span>Mindfulness Practices</span>
              </Link>
              <FontAwesomeIcon icon={faChevronRight} className="breadcrumb-separator" />
              <span className="breadcrumb-category">{cleanedCategory}</span>
              {creatorName && (
                <>
                  <FontAwesomeIcon icon={faChevronRight} className="breadcrumb-separator" />
                  <span className="breadcrumb-creator">{creatorName}</span>
                </>
              )}
              <FontAwesomeIcon icon={faChevronRight} className="breadcrumb-separator" />
              <span className="breadcrumb-current">{cleanedTitle}</span>
            </div>

            {/* Ambient Background Music Bar */}
            <div className="exercise-ambient-music-wrapper" style={{ marginBottom: "20px" }}>
              <MindfulnessMusicBar />
            </div>

            {/* Hero Card Section */}
            <div className="exercise-hero-card">
              <div className="exercise-hero-image-wrap">
                <img
                  className="exercise-hero-image"
                  src={data?.imageurl || ExerciseIllustration}
                  alt={cleanedTitle}
                  onError={(e) => {
                    e.currentTarget.onerror = null;
                    e.currentTarget.src = ExerciseIllustration;
                  }}
                />
              </div>

              <div className="exercise-hero-info">
                <div className="exercise-hero-header">
                  <div className="exercise-hero-meta-row">
                    <span className="exercise-category-pill">{cleanedCategory}</span>
                    {courtesyText && (
                      <span className="exercise-hero-courtesy-pill" title={courtesyText}>
                        <FontAwesomeIcon icon={faBookmark} style={{ marginRight: "5px" }} />
                        {courtesyText}
                      </span>
                    )}
                  </div>
                  <h1 className="exercise-title">{cleanedTitle}</h1>
                  {courtesyText && (
                    <div className="exercise-courtesy-attribution-row">
                      <span className="courtesy-attribution-label">Guidance Source:</span>
                      <span className="courtesy-attribution-highlight">{courtesyText}</span>
                    </div>
                  )}
                  <p className="exercise-summary">{cleanedSummary}</p>
                </div>

                {/* Inline Audio Player if toggled */}
                {showAudioPlayer && hasAudioSession && (
                  <div className="exercise-audio-player-banner">
                    <div className="audio-player-header">
                      <div className="audio-player-title">
                        <FontAwesomeIcon icon={faFileAudio} className="audio-icon" />
                        <span>Guided Audio Session</span>
                      </div>
                      <div className="audio-player-header-right">
                        {isAudioBuffering && (
                          <div className="audio-status-pill buffering" title="Loading audio stream...">
                            <FontAwesomeIcon icon={faSpinner} spin className="audio-status-spinner" />
                            <span>Loading audio...</span>
                          </div>
                        )}
                        {isPlayingAudio && !isAudioBuffering && (
                          <div className="audio-status-pill playing" title="Session playing">
                            <span className="audio-mini-wave wave-1"></span>
                            <span className="audio-mini-wave wave-2"></span>
                            <span className="audio-mini-wave wave-3"></span>
                            <span>Playing</span>
                          </div>
                        )}
                        {!isPlayingAudio && !isAudioBuffering && !audioErrorMsg && (
                          <div className="audio-status-pill ready" title="Ready to play">
                            <span>Ready</span>
                          </div>
                        )}
                        <button
                          type="button"
                          className="audio-player-close-btn"
                          onClick={() => {
                            setShowAudioPlayer(false);
                            setIsAudioBuffering(false);
                            setIsPlayingAudio(false);
                            resumeAfterGuidance();
                          }}
                          title="Close player"
                        >
                          <FontAwesomeIcon icon={faXmark} />
                        </button>
                      </div>
                    </div>

                    {isAudioBuffering && (
                      <div className="audio-buffering-notice">
                        <FontAwesomeIcon icon={faSpinner} spin className="buffering-notice-spinner" />
                        <span>Loading audio stream... Playback will begin in a moment.</span>
                      </div>
                    )}

                    {audioErrorMsg && (
                      <div className="audio-error-notice">
                        <span>{audioErrorMsg}</span>
                      </div>
                    )}

                    <AudioPlayer
                      autoPlay
                      src={activeAudioUrl}
                      progressJumpSteps={{ backward: 10000, forward: 10000 }}
                      onLoadStart={() => {
                        setIsAudioBuffering(true);
                        setAudioErrorMsg("");
                      }}
                      onLoadedData={() => {
                        setIsAudioBuffering(false);
                      }}
                      onCanPlay={() => {
                        setIsAudioBuffering(false);
                      }}
                      onWaiting={() => {
                        setIsAudioBuffering(true);
                      }}
                      onPlaying={() => {
                        setIsAudioBuffering(false);
                        setIsPlayingAudio(true);
                        pauseForGuidance();
                      }}
                      onPlay={pauseForGuidance}
                      onPause={() => {
                        setIsPlayingAudio(false);
                        resumeAfterGuidance();
                      }}
                      onEnded={() => {
                        setIsPlayingAudio(false);
                        setIsAudioBuffering(false);
                        resumeAfterGuidance();
                      }}
                      onError={() => {
                        setIsAudioBuffering(false);
                        setIsPlayingAudio(false);
                        setAudioErrorMsg("Audio stream could not be loaded. Please check your internet connection.");
                      }}
                    />
                  </div>
                )}

                {/* Actions Bar */}
                <div className="exercise-actions-bar">
                  <div className="exercise-left-actions">
                    {hasAudioSession && (
                      <button
                        type="button"
                        className={`exercise-play-btn ${showAudioPlayer ? "active" : ""}`}
                        onClick={() => {
                          setShowAudioPlayer((prev) => {
                            const next = !prev;
                            if (next) {
                              setIsAudioBuffering(true);
                              setAudioErrorMsg("");
                            } else {
                              setIsAudioBuffering(false);
                              setIsPlayingAudio(false);
                              resumeAfterGuidance();
                            }
                            return next;
                          });
                        }}
                      >
                        <FontAwesomeIcon
                          icon={
                            showAudioPlayer
                              ? faFileAudio
                              : isAudioBuffering
                              ? faSpinner
                              : faPlay
                          }
                          spin={showAudioPlayer && isAudioBuffering}
                        />
                        <span>
                          {showAudioPlayer
                            ? "Close Audio Player"
                            : "Play Mindfulness Guidance"}
                        </span>
                      </button>
                    )}
                  </div>

                  <div className="exercise-right-actions">
                    <button
                      type="button"
                      className={`exercise-action-btn ${
                        exerciseFavourited ? "favourited" : ""
                      }`}
                      onClick={handleFavourite}
                    >
                      <FontAwesomeIcon
                        icon={faStar}
                        className={exerciseFavourited ? "fav-star-active" : "fav-star"}
                      />
                      <span>
                        {exerciseFavourited ? "Favourited" : "Add to Favourites"}
                      </span>
                    </button>

                    <button
                      type="button"
                      className={`exercise-action-btn ${
                        exerciseCompleted ? "completed" : ""
                      }`}
                      onClick={completeExercise}
                      disabled={exerciseCompleted}
                    >
                      <FontAwesomeIcon
                        icon={exerciseCompleted ? faCheck : faCircleCheck}
                        className={exerciseCompleted ? "check-active" : ""}
                      />
                      <span>{exerciseCompleted ? "Completed" : "Mark as Complete"}</span>
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Main Details & Journal Section */}
            <div className="exercise-details-grid">
              {/* Left Column: Guidance Tabs & Content */}
              <div className="exercise-content-column">
                {data?.local === "yes" ? (
                  <>
                    {/* Modern Tabs Bar */}
                    <div className="exercise-tabs-container">
                      <button
                        type="button"
                        className={`exercise-tab-btn ${
                          activeTab === "overview" ? "active" : ""
                        }`}
                        onClick={() => setActiveTab("overview")}
                      >
                        Overview
                      </button>
                      <button
                        type="button"
                        className={`exercise-tab-btn ${
                          activeTab === "practice" ? "active" : ""
                        }`}
                        onClick={() => setActiveTab("practice")}
                      >
                        Mindfulness Guidance
                      </button>
                      {data?.homework?.sections?.length > 0 && (
                        <button
                          type="button"
                          className={`exercise-tab-btn ${
                            activeTab === "homework" ? "active" : ""
                          }`}
                          onClick={() => setActiveTab("homework")}
                        >
                          Mindfulness Homework
                        </button>
                      )}
                    </div>

                    {/* Active Tab Sections List */}
                    <div className="exercise-sections-list">
                      {currentSections.length > 0 ? (
                        currentSections.map((section, idx) => (
                          <BlogSection
                            key={section.number || idx}
                            sectionTitle={section.title}
                            sectionNumber={section.number}
                            sectionBody={section.body}
                            expand={true}
                          />
                        ))
                      ) : (
                        <div className="exercise-empty-section">
                          <p>No content available for this section.</p>
                        </div>
                      )}
                    </div>
                  </>
                ) : (
                  <div className="external-exercise-container">
                    {courtesyText && (
                      <div className="external-guidance-courtesy-banner">
                        <div className="courtesy-banner-icon">
                          <FontAwesomeIcon icon={faBookmark} />
                        </div>
                        <div className="courtesy-banner-content">
                          <div className="courtesy-banner-title">{courtesyText}</div>
                          <div className="courtesy-banner-text">
                            This guided practice is provided courtesy of {creatorName || "external guidance teachers"}.
                          </div>
                        </div>
                      </div>
                    )}
                    {isDirectAudio ? (
                      <div className="external-audio-card">
                        <div className="external-audio-card-header">
                          <div className="external-audio-card-icon">
                            <FontAwesomeIcon icon={faFileAudio} />
                          </div>
                          <div className="external-audio-card-info">
                            <div className="external-audio-card-title">{cleanedTitle}</div>
                            <div className="external-audio-card-sub">
                              {courtesyText || "Guided Audio Practice"} {data?.duration ? `• ${data.duration} mins` : ""}
                            </div>
                          </div>
                        </div>
                        <AudioPlayer
                          src={data.externalLink}
                          progressJumpSteps={{ backward: 10000, forward: 10000 }}
                          onLoadStart={() => setIsAudioBuffering(true)}
                          onLoadedData={() => setIsAudioBuffering(false)}
                          onCanPlay={() => setIsAudioBuffering(false)}
                          onWaiting={() => setIsAudioBuffering(true)}
                          onPlaying={() => {
                            setIsAudioBuffering(false);
                            setIsPlayingAudio(true);
                            pauseForGuidance();
                          }}
                          onPlay={pauseForGuidance}
                          onPause={() => {
                            setIsPlayingAudio(false);
                            resumeAfterGuidance();
                          }}
                          onEnded={() => {
                            setIsPlayingAudio(false);
                            setIsAudioBuffering(false);
                            resumeAfterGuidance();
                          }}
                          onError={() => {
                            setIsAudioBuffering(false);
                            setIsPlayingAudio(false);
                          }}
                        />
                      </div>
                    ) : data?.externalLink ? (
                      <div className="external-iframe-wrapper">
                        <iframe
                          ref={iframeRef}
                          className="external-iframe"
                          title={cleanedTitle}
                          src={youtubeEmbedUrl}
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                        />
                      </div>
                    ) : (
                      <div className="exercise-empty-section">
                        <p>No external resource link available.</p>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Right Column: Thought Capsule (Journal Notepad) */}
              <div className="exercise-journal-column">
                <div className="thought-capsule-card">
                  <div className="thought-capsule-header">
                    <div className="thought-capsule-icon-wrap">
                      <FontAwesomeIcon icon={faBookOpen} />
                    </div>
                    <div className="thought-capsule-headings">
                      <h3 className="thought-capsule-title">Thought Capsule</h3>
                      <p className="thought-capsule-desc">
                        Record what felt meaningful to you during this session.
                      </p>
                    </div>
                  </div>

                  <div className="thought-capsule-body">
                    <textarea
                      className="thought-capsule-textarea"
                      placeholder="Write your reflections, insights, or feelings here..."
                      value={journalText}
                      onChange={(e) => setJournalText(e.target.value)}
                      rows={8}
                    />
                  </div>

                  <div className="thought-capsule-footer">
                    <button
                      type="button"
                      className={`capsule-save-btn ${journalSaved ? "saved" : ""}`}
                      onClick={handleSaveJournal}
                    >
                      <FontAwesomeIcon icon={journalSaved ? faCheck : faBookmark} />
                      <span>{journalSaved ? "Saved!" : "Save To Journal"}</span>
                    </button>

                    <button
                      type="button"
                      className="capsule-download-btn"
                      onClick={downloadJournal}
                      title="Download journal as text file"
                    >
                      <FontAwesomeIcon icon={faDownload} />
                      <span>Download</span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
